import React, { useState } from 'react';
import { Card, Typography, Descriptions, Button, Form, Input, Space, Tag } from 'antd';
import { toast } from '../../../lib/toast';
import { useAuth } from '../hooks/useAuth';
import { AccountStatus, initiateTwoFactorApi } from '../api/auth';
import ChangePasswordCard from './ChangePasswordCard';
import PageScaffold from '../../../components/PageScaffold';
import { uxTokens } from '../../../design/ux';

const { Text } = Typography;

const ProfilePage: React.FC = () => {
  const { user, enableTwoFactor, disableTwoFactor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [initiating, setInitiating] = useState(false);

  if (!user) return null;

  const handleInitiate2FA = async () => {
    setInitiating(true);
    try {
      const result = await initiateTwoFactorApi();
      if (result.success) {
        setOtpSessionId(result.otpSessionId);
        toast.info('A verification code has been sent to your email');
      } else {
        toast.error(result.error);
      }
    } finally {
      setInitiating(false);
    }
  };

  const handleVerify2FA = async (values: { code: string }) => {
    if (!otpSessionId) return;
    setLoading(true);
    try {
      const result = await enableTwoFactor(otpSessionId, values.code);
      if (result.success) {
        toast.success('Two-factor authentication has been enabled');
        setOtpSessionId(null);
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    try {
      const result = await disableTwoFactor();
      if (result.success) {
        toast.success('Two-factor authentication has been disabled');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOtp = () => {
    setOtpSessionId(null);
  };

  return (
    <PageScaffold
      title="Profile"
      subtitle="Account details, password, and two-factor authentication."
    >
      <div className="cp-profile-body" style={{ maxWidth: 600, width: '100%' }}>
        <Card style={{ marginBottom: uxTokens.spacing.lg }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Name">
              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
          </Descriptions>
        </Card>

        <ChangePasswordCard
          disabled={user.accountStatus === AccountStatus.Pending}
        />

        <Card title="Two-Factor Authentication" style={{ marginTop: uxTokens.spacing.lg }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Add an extra layer of security to your account by requiring a verification
              code when signing in.
            </Text>

            {user.twoFactorEnabled ? (
              <>
                <Tag color="green">Enabled</Tag>
                <div>
                  <Button
                    danger
                    loading={loading}
                    onClick={handleDisable2FA}
                  >
                    Disable 2FA
                  </Button>
                </div>
              </>
            ) : otpSessionId ? (
              <>
                <Tag>Disabled</Tag>
                <Text style={{ display: 'block', marginTop: 8 }}>
                  Enter the 6-digit code sent to your email to enable 2FA.
                </Text>
                <Form layout="inline" onFinish={handleVerify2FA} style={{ marginTop: 8 }}>
                  <Form.Item
                    name="code"
                    rules={[
                      { required: true, message: 'Required' },
                      { len: 6, message: '6 digits' },
                    ]}
                  >
                    <Input.OTP length={6} />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Verify &amp; Enable
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button onClick={handleCancelOtp}>Cancel</Button>
                  </Form.Item>
                </Form>
              </>
            ) : (
              <>
                <Tag>Disabled</Tag>
                <div>
                  <Button
                    type="primary"
                    loading={initiating}
                    onClick={handleInitiate2FA}
                  >
                    Enable 2FA
                  </Button>
                </div>
              </>
            )}
          </Space>
        </Card>
      </div>
    </PageScaffold>
  );
};

export default ProfilePage;
